import type { AppRouteConfig, RouteFiltersConfig } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { FeatureIdEnum, FilterIdEnum } from '../../enums'
import {
  BrowseAnalytics,
  Categories,
  Conversational,
  Keywords,
  Now,
  Origins,
  Overall,
  Queries,
  RelatedPromptsAnalytics,
  Semantics,
  ToolReporting,
} from '../../views'
import { ROUTER_CONSTS } from '../router.consts'
import { COMPARE_FILTER_CONFIG } from '../routes.filters'

const PATHS = {
  root: 'insights',
  overall: 'overall',
  queries: 'queries',
  semantics: 'semantics',
  now: 'now',
  origins: 'origins',
  keywords: 'keywords',
  categories: 'categories',
  conversational: 'conversational',
  relatedPromptsAnalytics: 'relatedPromptsAnalytics',
  toolReporting: 'toolReporting',
}

const filters = (): RouteFiltersConfig => ({
  regularFiltersEnabled: true,
  config: {
    [FilterIdEnum.DATE]: {
      enabled: true,
      mandatory: true,
    },
  },
})

const searchFilters = (): RouteFiltersConfig => ({
  ...filters(),
  extra: {
    [COMPARE_FILTER_CONFIG.id]: COMPARE_FILTER_CONFIG,
  },
})

export const INSIGHTS_ROUTES: Record<string, Array<AppRouteConfig>> = {
  [DomainEnum.SEARCH]: [
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.NOW],
      path: PATHS.now,
      component: Now,
      meta: {
        featureId: FeatureIdEnum.NOW,
        filters: () => ({
          regularFiltersEnabled: true,
          config: {
            [FilterIdEnum.REALTIME]: {
              enabled: true,
              mandatory: true,
              intervalsInMinutes: [15, 60, 12 * 60],
            },
          },
        }),
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.ORIGINS],
      path: PATHS.origins,
      component: Origins,
      meta: {
        featureId: FeatureIdEnum.ORIGINS,
        filters: () => ({
          regularFiltersEnabled: true,
          config: {
            [FilterIdEnum.DATE]: {
              enabled: true,
              mandatory: true,
            },
          },
          initiallyHidden: true,
        }),
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.KEYWORDS],
      path: PATHS.keywords,
      component: Keywords,
      meta: {
        featureId: FeatureIdEnum.KEYWORDS,
        filters: () => ({
          regularFiltersEnabled: true,
          config: {
            [FilterIdEnum.DATE]: {
              enabled: true,
              mandatory: true,
            },
          },
        }),
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.CATEGORIES],
      path: PATHS.categories,
      component: Categories,
      meta: {
        featureId: FeatureIdEnum.CATEGORIES,
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.SEARCH],
      path: PATHS.overall,
      component: Overall,
      meta: {
        featureId: FeatureIdEnum.OVERALL,
        filters: searchFilters,
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.QUERIES],
      path: PATHS.queries,
      component: Queries,
      meta: {
        featureId: FeatureIdEnum.QUERIES,
        filters: searchFilters,
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.SEMANTICS],
      path: PATHS.semantics,
      component: Semantics,
      meta: {
        featureId: FeatureIdEnum.SEMANTICS,
        filters,
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_PROMPTS_ANALYTICS],
      path: PATHS.relatedPromptsAnalytics,
      component: RelatedPromptsAnalytics,
      meta: {
        featureId: FeatureIdEnum.RELATED_PROMPTS_ANALYTICS,
        filters,
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.TOOL_REPORTING],
      path: PATHS.toolReporting,
      component: ToolReporting,
      meta: {
        featureId: FeatureIdEnum.TOOL_REPORTING,
        filters: () => ({
          config: {
            [FilterIdEnum.DATE]: {
              enabled: true,
            },
          },
        }),
      },
    },
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.CONVERSATIONAL],
      path: PATHS.conversational,
      component: Conversational,
      meta: {
        featureId: FeatureIdEnum.CONVERSATIONAL,
        filters,
      },
    },
  ],
  [DomainEnum.BROWSE]: [
    {
      name: ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.BROWSE],
      path: PATHS.overall,
      component: BrowseAnalytics,
      meta: {
        featureId: FeatureIdEnum.OVERALL,
        filters: searchFilters,
      },
    },
  ],
}
